import React, { useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { X } from 'lucide-react';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';

export default function BottomSheet({ isOpen, onClose, title, children, size = 'default' }) {
    useEffect(() => {
        if (isOpen) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = '';
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <>
            <Overlay onClick={onClose} />
            <Sheet $size={size}>
                <SheetHandle />
                {title && (
                    <SheetHeader>
                        <SheetTitle>{title}</SheetTitle>
                        <CloseButton onClick={onClose}>
                            <X size={20} color={colors.night} />
                        </CloseButton>
                    </SheetHeader>
                )}
                <SheetContent>
                    {children}
                </SheetContent>
            </Sheet>
        </>
    );
}

const slideUp = keyframes`
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
`;

const slideUpCentered = keyframes`
    from { transform: translateX(-50%) translateY(100%); }
    to   { transform: translateX(-50%) translateY(0); }
`;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 100;
`;

const Sheet = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: ${colors.neutralLight};
    border-radius: 20px 20px 0 0;
    z-index: 101;
    animation: ${slideUp} 0.28s cubic-bezier(0.32, 0.72, 0, 1);
    max-height: 90vh;
    display: flex;
    flex-direction: column;

    ${({ $size }) => $size === 'large' && css`max-height: 95vh;`}
    ${({ $size }) => $size === 'small' && css`max-height: 50vh;`}

    @media screen and (min-width: ${breakpoints.lg}) {
        max-width: 480px;
        left: 50%;
        transform: translateX(-50%);
        border-radius: 20px 20px 0 0;
        animation-name: ${slideUpCentered};
    }
`;

const SheetHandle = styled.div`
    width: 40px;
    height: 4px;
    background: ${colors.nightLight};
    border-radius: 2px;
    margin: 12px auto 4px;
    flex-shrink: 0;
`;

const SheetHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    flex-shrink: 0;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const SheetTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
`;

const SheetContent = styled.div`
    overflow-y: auto;
    padding: 16px 20px 32px;
    flex: 1;
`;
